package com.bikompanion.data.gbfs

import kotlinx.serialization.KSerializer
import kotlinx.serialization.descriptors.PrimitiveKind
import kotlinx.serialization.descriptors.PrimitiveSerialDescriptor
import kotlinx.serialization.encoding.Decoder
import kotlinx.serialization.encoding.Encoder
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonDecoder
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.jsonPrimitive

/**
 * Preferred language, where a feed offers a choice.
 *
 * Only affects feeds that genuinely translate. Bicing publishes the same Catalan street name
 * under all five of its languages, and AMBici's station names are already bilingual in place.
 */
internal const val GBFS_PREFERRED_LANGUAGE = "en"

/**
 * Reads a GBFS human-readable string across the versions we consume.
 *
 * GBFS 3.0 made every such field a list of `{text, language}`; 2.x had a bare string. The two
 * Barcelona operators sit either side of that change — Bicing publishes 3.0, AMBici 2.3 — so
 * a reader that only understands one of them can only ever see half the map.
 *
 * Read-only in practice; [serialize] exists because the interface demands it and is what the
 * on-disk cache would use if a DTO were ever written back out.
 */
internal object GbfsTextSerializer : KSerializer<String> {
    override val descriptor =
        PrimitiveSerialDescriptor("GbfsText", PrimitiveKind.STRING)

    override fun deserialize(decoder: Decoder): String {
        val json = decoder as? JsonDecoder ?: return decoder.decodeString()
        return when (val element = json.decodeJsonElement()) {
            // GBFS 2.x, and any 3.0 field an operator has left as a bare string.
            is JsonPrimitive -> element.content

            is JsonArray -> element.pickLocalized()

            // A field that is neither is one we can't render; an empty name beats a crash.
            else -> ""
        }
    }

    override fun serialize(
        encoder: Encoder,
        value: String,
    ) = encoder.encodeString(value)

    private fun JsonArray.pickLocalized(): String {
        val entries = mapNotNull { it as? JsonObject }
        val preferred = entries.firstOrNull {
            it["language"]?.jsonPrimitive?.content == GBFS_PREFERRED_LANGUAGE
        }
        return (preferred ?: entries.firstOrNull())
            ?.get("text")
            ?.jsonPrimitive
            ?.content
            .orEmpty()
    }
}
